import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ApiError } from '@/lib/api-client';
import { PublicShell } from '@/components/layout/PublicShell';

type Status = 'verifying' | 'success' | 'expired' | 'used' | 'invalid';

export function VerifyEmailPage() {
  const { token } = useParams({ from: '/verify-email/$token' });
  const [status, setStatus] = useState<Status>('verifying');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/api/auth/verify-email/${token}`);
        if (!cancelled) setStatus('success');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === 'expired') setStatus('expired');
          else if (err.code === 'already_used') setStatus('used');
          else setStatus('invalid');
        } else {
          setStatus('invalid');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const messages: Record<Status, { title: string; desc: string; tone: 'info' | 'success' | 'error' }> = {
    verifying: { title: 'Doğrulanıyor…', desc: 'Bağlantı kontrol ediliyor.', tone: 'info' },
    success: { title: 'E-posta doğrulandı', desc: 'Hesabınız aktif. Dashboard\'a geçebilirsiniz.', tone: 'success' },
    expired: { title: 'Bağlantı süresi dolmuş', desc: 'Yeni bir doğrulama e-postası isteyin.', tone: 'error' },
    used: { title: 'Bağlantı kullanılmış', desc: 'Bu e-posta zaten doğrulanmış olabilir.', tone: 'error' },
    invalid: { title: 'Bağlantı geçersiz', desc: 'Bu doğrulama bağlantısı tanınmıyor.', tone: 'error' },
  };

  const m = messages[status];

  return (
    <PublicShell>
      <div className="container flex items-center justify-center py-12">
        <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{m.title}</CardTitle>
          <CardDescription>{m.desc}</CardDescription>
        </CardHeader>
        {status !== 'verifying' && (
          <>
            <CardContent>
              <div
                className={
                  m.tone === 'success'
                    ? 'rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900'
                    : 'rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive'
                }
              >
                {m.desc}
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button asChild>
                <Link to={status === 'success' ? '/dashboard' : '/login'}>
                  {status === 'success' ? 'Dashboard' : 'Girişe Dön'}
                </Link>
              </Button>
            </CardFooter>
          </>
        )}
        </Card>
      </div>
    </PublicShell>
  );
}
